import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * The permission matrix (8 Aug): the whole TrustCare vocabulary as one
 * read-only grid - every permission, every role's default, the floors, and
 * which keys the engine already enforces. Per-person changes stay where
 * governance lives: the override editor on each user in Users & access.
 */

interface MatrixEntry {
    key: string;
    label: string;
    module: string;
    floor: boolean;
    enforced: boolean;
    defaults: string[];
    note: string | null;
}

const ROLES = ["admin", "manager", "supervisor", "staff", "partner", "viewer", "support"] as const;

export const PermissionMatrix = () => {
    const [entries, setEntries] = useState<MatrixEntry[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        api.get<{ permissions: MatrixEntry[] }>("/quality-permissions/catalog")
            .then((c) => setEntries(c.permissions))
            .catch(() => setError("Could not load the permission matrix"));
    }, []);

    if (error) return <div className="card"><div className="card-body"><p className="empty">{error}</p></div></div>;
    if (!entries) return <div className="card"><div className="card-body"><p className="empty">Loading…</p></div></div>;

    const modules = [...new Set(entries.map((e) => e.module))];

    return (
        <div className="card">
            <div className="card-head">
                <div>
                    <h2>Permission matrix</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        Every permission in the CRM and each role&rsquo;s default. TrustCare owns this
                        vocabulary — roles are composed from it, and a per-person override (set on the
                        user in Users &amp; access) changes one cell of it for one person, with a reason.
                        A dimmed row documents today&rsquo;s role rule but its screens have not adopted
                        the engine yet; a &ldquo;floor&rdquo; can never be denied.
                    </p>
                </div>
            </div>
            <div className="card-body flush">
                <div className="table-wrap">
                    <table style={{ fontSize: "0.85rem" }}>
                        <thead><tr>
                            <th style={{ minWidth: "16rem" }}>Permission</th>
                            {ROLES.map((r) => <th key={r} style={{ textTransform: "capitalize" }}>{r}</th>)}
                        </tr></thead>
                        <tbody>
                            {modules.map((mod) => (
                                <>
                                    <tr key={mod}>
                                        <td colSpan={ROLES.length + 1} style={{ fontWeight: 700,
                                            fontSize: "0.72rem", textTransform: "uppercase",
                                            letterSpacing: "0.06em", color: "var(--muted)",
                                            paddingTop: "0.7rem" }}>
                                            {mod}
                                        </td>
                                    </tr>
                                    {entries.filter((e) => e.module === mod).map((e) => (
                                        <tr key={e.key} style={e.enforced ? undefined : { opacity: 0.55 }}
                                            title={e.note ?? undefined}>
                                            <td>
                                                {e.label}
                                                {e.floor && (
                                                    <span className="chip mute" style={{ marginLeft: 5 }}
                                                        title="Reporting is never blocked — this permission cannot be denied.">
                                                        floor
                                                    </span>
                                                )}
                                                {!e.enforced && (
                                                    <span className="chip mute" style={{ marginLeft: 5 }}
                                                        title="Documents today's role rule; the module has not adopted the engine yet.">
                                                        role-checked
                                                    </span>
                                                )}
                                            </td>
                                            {ROLES.map((r) => {
                                                const holds = r === "admin" || e.defaults.includes(r);
                                                return (
                                                    <td key={r} aria-label={`${e.key} ${r} ${holds ? "yes" : "no"}`}
                                                        style={{ textAlign: "center" }}>
                                                        {holds ? "✓" : "—"}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
